import React, { useEffect, useState } from "react";
import { X, Loader2, Plus, FolderPlus, Check } from "lucide-react";
import api from "../lib/api";
import { useCrypto } from "../context/CryptoContext";

// Small picker used on a member's own profile posts to file a post into one of
// their collections (or spin up a new one). One collection per post.
export const AddToCollectionModal = ({ post, myId, onClose, onChanged }) => {
  const { encryptNetText, decryptNetText, hasNetwork } = useCrypto();
  const [cols, setCols] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const decorate = async (list) => {
    const out = [];
    for (const c of list) {
      let title = c.title || "Untitled collection";
      if (c.enc && c.title_ct) { const t = await decryptNetText(c.title_ct, c.title_iv); title = t || "Untitled collection"; }
      out.push({ ...c, _title: title });
    }
    return out;
  };

  const load = () => api.get(`/collections?owner=${myId}`).then(async ({ data }) => setCols(await decorate(data))).catch(() => setCols([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addTo = async (cid) => {
    setBusy(true);
    try { await api.post(`/collections/${cid}/posts`, { post_id: post.id }); onChanged && onChanged(); onClose(); }
    catch (e) { alert(e?.response?.data?.detail || "Could not add to collection."); setBusy(false); }
  };

  const removeFrom = async () => {
    setBusy(true);
    try { await api.delete(`/collections/${post.collection_id}/posts/${post.id}`); onChanged && onChanged(); onClose(); }
    catch { alert("Could not remove from collection."); setBusy(false); }
  };

  const createAndAdd = async () => {
    if (!newTitle.trim()) return;
    setBusy(true);
    try {
      let payload = { title: newTitle.trim(), enc: false };
      if (hasNetwork()) { const e = await encryptNetText(newTitle.trim()); payload = { title_ct: e.ct, title_iv: e.iv, enc: true, title: "" }; }
      const { data } = await api.post("/collections", payload);
      await api.post(`/collections/${data.id}/posts`, { post_id: post.id });
      onChanged && onChanged(); onClose();
    } catch (e) { alert(e?.response?.data?.detail || "Could not create collection."); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="add-to-collection-modal">
      <div className="bg-[#121212] border border-white/10 w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-heading text-base text-white flex items-center gap-2"><FolderPlus size={16} className="text-[#14A89A]" /> Add to a collection</h3>
          <button onClick={onClose} data-testid="close-add-to-collection" className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {post.collection_id && (
            <div className="flex items-center justify-between gap-3 border border-[#14A89A]/40 bg-[#14A89A]/5 px-3 py-2.5">
              <span className="font-body text-xs text-zinc-300">This post is already in a collection.</span>
              <button onClick={removeFrom} disabled={busy} data-testid="remove-from-collection" className="font-body text-xs text-red-400 hover:text-red-300">Remove</button>
            </div>
          )}

          {cols === null ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#14A89A]" size={18} /></div>
          ) : (
            <div className="space-y-2" data-testid="collection-choices">
              {cols.map((c) => (
                <button key={c.id} onClick={() => addTo(c.id)} disabled={busy || post.collection_id === c.id} data-testid={`choose-collection-${c.id}`} className={`w-full flex items-center justify-between gap-3 border px-3 py-3 font-body text-sm transition-colors text-left ${post.collection_id === c.id ? "border-[#14A89A]/40 text-[#14A89A]" : "border-white/12 text-zinc-200 hover:border-[#14A89A] hover:text-white"}`}>
                  <span className="truncate">{c._title}</span>
                  {post.collection_id === c.id ? <Check size={15} /> : <Plus size={15} className="shrink-0 text-zinc-500" />}
                </button>
              ))}
              {cols.length === 0 && <p className="font-body text-xs text-zinc-500 py-2">You don't have any collections yet, create your first below.</p>}
            </div>
          )}

          {creating ? (
            <div className="space-y-2 border-t border-white/10 pt-4">
              <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Name your collection (e.g. 30-Day Garden Series)" data-testid="new-collection-input" className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body text-sm" />
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => setCreating(false)} className="font-body text-xs text-zinc-400 hover:text-white px-3 py-1.5">Cancel</button>
                <button onClick={createAndAdd} disabled={busy || !newTitle.trim()} data-testid="create-collection-confirm" className="font-body text-xs bg-[#14A89A] text-[#0A0A0A] font-medium px-4 py-1.5 hover:bg-[#2BC4B5] transition-colors flex items-center gap-1.5 disabled:opacity-40">{busy && <Loader2 size={12} className="animate-spin" />} Create &amp; add</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setCreating(true)} data-testid="new-collection-toggle" className="w-full border border-dashed border-white/20 hover:border-[#14A89A] text-zinc-300 hover:text-white py-3 flex items-center justify-center gap-2 font-body text-sm transition-colors"><Plus size={15} /> New collection</button>
          )}
        </div>
      </div>
    </div>
  );
};
